import {
  Badge,
  Button,
  Flex,
  HStack,
  Icon,
  Link,
  Spacer,
  Text,
} from '@chakra-ui/react'
import React, { useContext } from 'react'
import type { UseComboboxReturnValue } from 'downshift'
import { Trans, useTranslation } from 'react-i18next'
import { SemanticSearchStars } from '../../../../graphics/media-library/SemanticSearchStars'
import { FeaturesEnum } from '../../../../utils/features'
import type { ListItemProps } from '../../../interfaces'
import { UserContext } from '../../../providers'

interface Props {
  page: string
  search: string
  item: ListItemProps
  index: number
  getItemProps: UseComboboxReturnValue<ListItemProps>['getItemProps']
}

export const SemanticSearchBar: React.FC<Props> = ({
  page,
  search,
  item,
  index,
  getItemProps,
}) => {
  const { user } = useContext(UserContext)
  const { t } = useTranslation()

  if (page === 'media-collection') {
    return null
  }

  if (user?.pricingPlan.featuresList.includes(FeaturesEnum.SEMANTIC_SEARCH)) {
    return (
      <Flex py="16px" px="16px" w="full">
        <HStack
          bgGradient="linear(to-l, #D6E0FF, #E6F9FE)"
          w="full"
          borderColor="#CDDCF7"
          borderWidth="1px"
          borderRadius={8}
          py="8px"
          px="12px"
          alignItems="flex-start"
        >
          <Icon as={SemanticSearchStars} w="20px" h="20px" color="#6896E6" />
          <Flex direction="column">
            <Text
              style={{
                lineBreak: 'anywhere',
              }}
            >
              Search for images related to
              <Text as="span" fontWeight="semibold" ml="1">
                "{search}"
              </Text>
            </Text>
            <Text>
              <Badge display="inline-block" variant="imagekitWhite">
                {t('ai_powered', 'AI Powered')}
              </Badge>
            </Text>
          </Flex>
          <Spacer />
          <Button
            minW="100px"
            colorScheme="appSemanticSearch"
            alignSelf="center"
            fontWeight="500"
            {...getItemProps({
              item,
              index,
            })}
          >
            <Text>{t('search')}</Text>
          </Button>
        </HStack>
      </Flex>
    )
  }

  return (
    <Flex py="16px" px="16px" w="full">
      <HStack
        bgGradient="linear(to-l, #D6E0FF, #E6F9FE)"
        w="full"
        borderColor="#CDDCF7"
        borderWidth="1px"
        borderRadius={8}
        py="8px"
        px="12px"
        alignItems="flex-start"
      >
        <Icon as={SemanticSearchStars} w="20px" h="20px" color="#6896E6" />
        <Flex direction="column">
          <Trans i18nKey="semantic_search_bar_heading_free" t={t}>
            <Text
              style={{
                lineBreak: 'anywhere',
              }}
              fontWeight="semibold"
            >
              Unlock visual search
            </Text>
          </Trans>
          <Trans i18nKey="semantic_search_bar_sub_heading_free" t={t}>
            <Text
              style={{
                lineBreak: 'anywhere',
              }}
            >
              Level up your searches with AI precision and unlock a smarter way
              to explore images
            </Text>
          </Trans>
        </Flex>
        <Spacer />
        <Button
          as={Link}
          minW="100px"
          colorScheme="appSemanticSearch"
          fontWeight="500"
          alignSelf="center"
          href="https://docs.imagekit.io/media-library/overview/search-update-and-delete#visual-search"
          isExternal
          _hover={{
            textDecoration: 'none',
          }}
        >
          <Text>{t('learn_more')}</Text>
        </Button>
      </HStack>
    </Flex>
  )
}
